import React, { useState, useRef, useEffect } from 'react';
import { cns } from '../../utils';
import css from './InputDropDown.module.scss';
import { ClickAwayListener } from '@mui/material';
import Chevron from '../../assets/chevron-left.svg';

interface Option<T> {
    id: T;
    label: string;
}

interface InputDropdownProps<T> {
    options: Option<T>[];
    value: T | '';
    onChange: (value: T | string) => void;
    placeholder?: string;
    className?: string;
}

export function InputDropDown<T>({ options, value, onChange, placeholder, className }: InputDropdownProps<T>) {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value ? String(value) : '');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setInputValue(value ? String(value) : '');
    }, [value]);

    const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()));

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        setIsOpen(true);
        onChange(e.target.value);
    };

    const handleSelect = (option: Option<T>) => {
        setInputValue(option.label);
        setIsOpen(false);
        onChange(option.id);
        if (inputRef.current) {
            inputRef.current.blur();
        }
    };

    return (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <div className={cns(css.inputDropdownContainer, className)}>
                <div className={css.inputWrapper}>
                    <input
                        ref={inputRef}
                        className={css.input}
                        value={inputValue}
                        placeholder={placeholder}
                        onFocus={() => setIsOpen(true)}
                        onClick={() => setIsOpen(true)}
                        onChange={handleInputChange}
                        autoComplete="off"
                    />
                </div>
                <button
                    className={css.chevronButton}
                    type="button"
                    onClick={() => {
                        setIsOpen((prevVal) => !prevVal);
                    }}
                >
                    <div className={cns(css.chevron, isOpen && css.flipped)}>
                        <Chevron />
                    </div>
                </button>
                {isOpen && filteredOptions.length > 0 && (
                    <div className={css.dropdownMenu}>
                        {filteredOptions.map((option) => (
                            <button
                                key={String(option.id)}
                                className={css.dropdownItem}
                                type="button"
                                onClick={() => handleSelect(option)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </ClickAwayListener>
    );
}
